import React, { useEffect, useState } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, doc, updateDoc, orderBy, query, deleteField, setDoc } from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const AdminUserDashboard = () => {
  const [users, setUsers] = useState([]);
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    companyName: '',
    groupName: '',
    role: 'user',
    status: 'active',
  });

  const fetchUsers = async () => {
    const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    const userList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setUsers(userList);
  };

  const updateUser = async (userId, updates) => {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, updates);
    fetchUsers();
  };

  // ✅ One-time migration function
  const migrateUsers = async () => {
    const q = query(collection(db, 'users'));
    const snapshot = await getDocs(q);

    for (const userDoc of snapshot.docs) {
      const data = userDoc.data();
      const updates = {};

      if (data.hasOwnProperty('isAdmin')) {
        updates.role = data.isAdmin ? 'admin' : 'user';
      }

      if (data.accessTier === 'expired') {
        updates.status = 'suspended';
      } else if (data.accessTier === 'free' || data.accessTier === 'paid') {
        updates.status = 'active';
      }

      const userRef = doc(db, 'users', userDoc.id);
      await updateDoc(userRef, {
        ...updates,
        isAdmin: deleteField(),
        accessTier: deleteField()
      });

      console.log(`✅ Migrated: ${userDoc.id}`);
    }

    fetchUsers();
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <div style={{ padding: '40px' }}>
      <h2>Admin User Management</h2>

      {/* ✅ Temporary migration button */}

      <div style={{ marginBottom: '40px' }}>
        <h3 style={{ marginBottom: '16px', fontSize: '20px' }}>➕ Add New User</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '12px' }}>
          {['name', 'email', 'companyName', 'groupName'].map((field) => (
            <input
              key={field}
              placeholder={field.replace(/([A-Z])/g, ' $1')}
              value={newUser[field]}
              onChange={(e) => setNewUser({ ...newUser, [field]: e.target.value })}
              style={{
                padding: '12px 16px',
                borderRadius: '12px',
                border: '1px solid #ccc',
                minWidth: '200px',
                fontSize: '14px',
                fontFamily: 'Helvetica Neue, sans-serif',
              }}
            />
          ))}
          <select
            value={newUser.role}
            onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
            style={{
              padding: '12px',
              borderRadius: '12px',
              border: '1px solid #ccc',
              fontSize: '14px',
              fontFamily: 'Helvetica Neue, sans-serif',
            }}
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
          <select
            value={newUser.status}
            onChange={(e) => setNewUser({ ...newUser, status: e.target.value })}
            style={{
              padding: '12px',
              borderRadius: '12px',
              border: '1px solid #ccc',
              fontSize: '14px',
              fontFamily: 'Helvetica Neue, sans-serif',
            }}
          >
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
          <button
            style={{
              padding: '12px 20px',
              background: '#FFD700',
              color: '#000',
              border: 'none',
              borderRadius: '12px',
              fontWeight: '600',
              fontSize: '14px',
              fontFamily: 'Helvetica Neue, sans-serif',
              cursor: 'pointer',
            }}
            onClick={async () => {
              if (!newUser.email || !newUser.name) {
                alert('Name and email are required.');
                return;
              }

              try {
                const functions = getFunctions();
                const createUser = httpsCallable(functions, 'createUser');
                const response = await createUser({
                  email: newUser.email,
                  displayName: newUser.name,
                  companyName: newUser.companyName,
                  groupName: newUser.groupName,
                  role: newUser.role,
                  status: newUser.status
                });

                if (!response.data.success) {
                  throw new Error(response.data.error);
                }

                const userRef = doc(db, 'users', newUser.email);
                await setDoc(userRef, {
                  ...newUser,
                  createdAt: Timestamp.now(),
                });

                setNewUser({
                  name: '',
                  email: '',
                  companyName: '',
                  groupName: '',
                  role: 'user',
                  status: 'active',
                });

                fetchUsers();
                alert('✅ User successfully created!');
              } catch (err) {
                console.error('Create user error:', err);
                alert(`Failed to create user: ${err.message}`);
              }
            }}
          >
            Create User
          </button>
        </div>
      </div>

      <button
        onClick={migrateUsers}
        style={{ marginBottom: '20px', background: '#222', color: '#fff', padding: '10px 20px', borderRadius: '8px' }}
      >
        Run User Migration
      </button>

      <table style={{ width: '100%', marginTop: '24px', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #ccc' }}>
            <th style={{ textAlign: 'left', padding: '12px' }}>Email</th>
            <th>Company</th>
            <th>Group</th>
            <th>Role</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '12px' }}>{u.email}</td>
              <td style={{ textAlign: 'center' }}>{u.companyName || '—'}</td>
              <td style={{ textAlign: 'center' }}>{u.groupName || '—'}</td>
              <td style={{ textAlign: 'center' }}>
                <select
                  value={u.role || 'user'}
                  onChange={(e) => updateUser(u.id, { role: e.target.value })}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </td>
              <td style={{ textAlign: 'center' }}>
                <select
                  value={u.status || 'active'}
                  onChange={(e) => updateUser(u.id, { status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </select>
              </td>
              <td style={{ textAlign: 'center' }}>
                {u.createdAt?.toDate().toLocaleDateString() || '–'}
              </td>
              <td style={{ textAlign: 'center' }}>
                <button
                  onClick={() =>
                    updateUser(u.id, { status: u.status === 'active' ? 'suspended' : 'active' })
                  }
                >
                  {u.status === 'active' ? 'Suspend' : 'Activate'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminUserDashboard;
